import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { getCategories } from "./categories";
import { useAppData } from "./store";
import { Save, X } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

/* ---------- Novo / Editar CARTÃO ---------- */
export const CartaoModal = ({ open, onOpenChange, item }) => {
  const { addCartao, updateCartao } = useAppData();
  const empty = { nome: "", final: "", fechamento: 1, vencimento: 10, limite: "" };
  const [form, setForm] = useState(empty);

  useEffect(() => { if (open) setForm(item || empty); /* eslint-disable-line */ }, [open, item]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.error("Informe o nome do cartão.");
    if (!/^\d{4}$/.test(String(form.final))) return toast.error("Final do cartão precisa ter 4 dígitos.");
    const limiteNum = Number(String(form.limite || "0").replace(",", ".")) || 0;
    const payload = { ...form, limite: limiteNum };
    if (item) { updateCartao(item.id, payload); toast.success("Cartão atualizado!"); }
    else { addCartao(payload); toast.success("Cartão criado!"); }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cartao-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{item ? "Editar cartão" : "Novo cartão"}</DialogTitle>
          <DialogDescription>Cadastre o cartão para registrar despesas e parcelas.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Nome do cartão</label>
            <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Itaú Platinum Visa" data-testid="cartao-nome" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Final</label>
              <Input value={form.final} onChange={(e) => setForm({ ...form, final: e.target.value })} placeholder="0000" maxLength={4} inputMode="numeric" data-testid="cartao-final" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Fecha dia</label>
              <Input type="number" min={1} max={31} value={form.fechamento} onChange={(e) => setForm({ ...form, fechamento: Number(e.target.value) })} data-testid="cartao-fechamento" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Vence dia</label>
              <Input type="number" min={1} max={31} value={form.vencimento} onChange={(e) => setForm({ ...form, vencimento: Number(e.target.value) })} data-testid="cartao-vencimento" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Limite do cartão (R$)</label>
            <Input type="text" inputMode="decimal" value={form.limite} onChange={(e) => setForm({ ...form, limite: e.target.value })} placeholder="Ex: 5000,00 (deixe em branco se não souber)" data-testid="cartao-limite" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1"><X className="h-4 w-4 mr-1.5" /> Cancelar</Button>
            <Button type="submit" className="flex-1 monyx-btn-primary text-white" data-testid="cartao-save"><Save className="h-4 w-4 mr-1.5" /> Salvar</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Nova / Editar TRANSAÇÃO de cartão ---------- */
export const CartaoTxModal = ({ open, onOpenChange, cartaoId, item }) => {
  const { data, addCartaoTx, updateCartaoTx } = useAppData();
  const empty = { data: today(), desc: "", cat: "ifood", valor: "", parcelas: 1, proxima: "—" };
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (open) {
      if (item) {
        const numParcelas = item.parcelas ? parseInt(String(item.parcelas).split("/")[1] || "1", 10) : 1;
        const totalValor = Number(item.valor || 0) * numParcelas;
        setForm({ ...item, valor: String(totalValor), parcelas: numParcelas });
      } else {
        setForm(empty); // eslint-disable-line
      }
    }
  }, [open, item]); // eslint-disable-line

  const submit = (e) => {
    e.preventDefault();
    if (!form.desc.trim()) return toast.error("Informe a descrição.");
    const totalValor = Number(String(form.valor).replace(",", "."));
    if (!totalValor || totalValor <= 0) return toast.error("Valor inválido.");
    const numParcelas = Math.max(1, Math.round(Number(form.parcelas) || 1));
    const valorParcela = totalValor / numParcelas;
    const payload = { ...form, valor: valorParcela, parcelas: `1/${numParcelas}` };
    if (item) { updateCartaoTx(cartaoId, item.id, payload); toast.success("Transação atualizada!"); }
    else { addCartaoTx(cartaoId, payload); toast.success("Transação adicionada!"); }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cartao-tx-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{item ? "Editar transação" : "Nova transação"}</DialogTitle>
          <DialogDescription>Compra ou parcelamento no cartão selecionado.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Data</label>
              <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="tx-data" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Valor (R$)</label>
              <Input type="text" inputMode="decimal" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} placeholder="0,00" data-testid="tx-valor" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Descrição</label>
            <Input value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="Ex: AMAZON.COM" data-testid="tx-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Categoria</label>
              <select value={form.cat} onChange={(e) => setForm({ ...form, cat: e.target.value })} className="mt-1.5 w-full h-10 rounded-md bg-slate-900 border border-slate-700 px-3 text-sm" data-testid="tx-cat">
                {getCategories(data?.customCategories).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Parcelas</label>
              <Input type="number" min="1" max="60" value={form.parcelas} onChange={(e) => setForm({ ...form, parcelas: e.target.value })} placeholder="1" data-testid="tx-parcelas" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1"><X className="h-4 w-4 mr-1.5" /> Cancelar</Button>
            <Button type="submit" className="flex-1 monyx-btn-primary text-white" data-testid="tx-save"><Save className="h-4 w-4 mr-1.5" /> Salvar</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Importar CSV ---------- */
export const ImportCsvModal = ({ open, onOpenChange, cartaoId }) => {
  const { data, importCartaoCSV } = useAppData();
  const [text, setText] = useState("");
  const [preview, setPreview] = useState([]);

  useEffect(() => { if (!open) { setText(""); setPreview([]); } }, [open]);

  const parse = (raw) => {
    const lines = raw.trim().split(/\r?\n/).filter(Boolean);
    const out = [];
    for (const line of lines) {
      // Accept ',' or ';' separator. Format: data;desc;valor[;categoria;parcelas]
      const parts = line.split(/[;,\t]/).map((p) => p.trim());
      if (parts.length < 3) continue;
      const [dataRaw, desc, valorRaw, catRaw, parcelas] = parts;
      // accept dd/mm/yyyy or yyyy-mm-dd
      let data = dataRaw;
      const m = dataRaw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (m) data = `${m[3]}-${m[2]}-${m[1]}`;
      const valor = Number(String(valorRaw).replace(/[R$\s.]/g, "").replace(",", "."));
      if (!desc || !valor) continue;
      const cat = (catRaw && getCategories(data?.customCategories).find((c) => c.id === catRaw.toLowerCase())) ? catRaw.toLowerCase() : "ifood";
      out.push({ data, desc, valor, cat, parcelas: parcelas || "1/1", proxima: "—" });
    }
    return out;
  };

  const onTextChange = (val) => {
    setText(val);
    setPreview(parse(val));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const txt = await file.text();
    onTextChange(txt);
  };

  const confirm = () => {
    if (preview.length === 0) return toast.error("Cole/anexe linhas válidas para importar.");
    importCartaoCSV(cartaoId, preview);
    toast.success(`${preview.length} transação(ões) importada(s)!`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="csv-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Importar CSV — Cartão</DialogTitle>
          <DialogDescription>
            Formato por linha: <code className="px-1.5 py-0.5 rounded bg-slate-800 text-emerald-300 text-xs">data;descrição;valor;categoria;parcelas</code> — separadores aceitos: <b>; , Tab</b>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input type="file" accept=".csv,.txt" onChange={onFile} className="text-xs text-slate-300" data-testid="csv-file" />
            <span className="text-xs text-slate-500">ou cole abaixo</span>
          </div>
          <textarea
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder={"15/02/2026;UBER TRIP;42,80;transporte;1/1\n16/02/2026;NETFLIX;55,90;assinaturas;1/1"}
            className="w-full h-40 rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm font-mono focus:outline-none focus:border-emerald-500/40"
            data-testid="csv-textarea"
          />

          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Pré-visualização ({preview.length} linhas)</div>
            <div className="mt-2 max-h-40 overflow-y-auto divide-y divide-slate-800">
              {preview.length === 0 && <div className="text-xs text-slate-500 py-2">Nenhuma linha válida ainda.</div>}
              {preview.slice(0, 20).map((tx, i) => (
                <div key={i} className="flex items-center justify-between gap-2 py-2 text-xs">
                  <span className="text-slate-400 tabular-nums w-20">{tx.data}</span>
                  <span className="text-white flex-1 truncate">{tx.desc}</span>
                  <span className="text-slate-300">{tx.cat}</span>
                  <span className="text-emerald-300 font-semibold tabular-nums">R$ {tx.valor.toFixed(2)}</span>
                </div>
              ))}
              {preview.length > 20 && <div className="text-[11px] text-slate-500 pt-1">… +{preview.length - 20} linhas</div>}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1"><X className="h-4 w-4 mr-1.5" /> Cancelar</Button>
            <Button onClick={confirm} disabled={!preview.length} className="flex-1 monyx-btn-primary text-white" data-testid="csv-import">
              Importar {preview.length > 0 && `(${preview.length})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
